import { useCallback } from "react";
import { gql, useMutation } from "@apollo/client";

import { useGetMenuCategories } from "./use-get-menu-categories";
import { handleErrorMessage } from "@/utils/error-handler";

const MUTATION = gql`
	mutation UpdateMenu($categories: [MenuCategoryUpdateInput!]!) {
		updateMenu(categories: $categories)
	}
`;

type Props = ReturnType<typeof useGetMenuCategories>["categories"];
type Payload = {
	id: string;
	showInMenu: boolean;
	items: {
		id: string;
		showInMenu: boolean;
	}[];
	groups: {
		id: string;
		showInMenu: boolean;
		items: {
			id: string;
			showInMenu: boolean;
		}[];
	}[];
}[];

function preparePayload(categories: Props): Payload {
	return categories.map((category) => ({
		id: category.id,
		showInMenu: category.showInMenu,
		items: category.items
			.filter((item) => !("showAs" in item))
			.map((item) => ({
				id: item.id,
				showInMenu: item.showInMenu,
			})),
		groups: category.items
			.filter((item) => "showAs" in item)
			.map((group) => ({
				id: group.id,
				showInMenu: group.showInMenu,
				items: group.items.map((item) => ({
					id: item.id,
					showInMenu: item.showInMenu,
				})),
			})),
	}));
}

export function useUpdateMenu() {
	const [update, { loading }] = useMutation<
		{ ok: boolean },
		{ categories: Payload }
	>(MUTATION);

	const updateMenu = useCallback(
		async (variables: Props) => {
			try {
				const payload = preparePayload(variables);

				const { data } = await update({
					variables: { categories: payload },
				});
				return data?.ok ?? false;
			} catch (error) {
				handleErrorMessage(error);
			}
		},
		[update],
	);

	return { execute: updateMenu, loading };
}
